/* global React, Icon, Card, Button, EmptyState, Segmented, Switch, KPI, toast, fmt, fmt0 */
// ============================================================
// PLUMME 2.0 — AI konsulten · Rapporter (bedrift)
// Måneds-/årsrapport med periode-sammenligning på tvers av år og måned.
// Eksporteres som window.AiReport (brukes av aiinsikt.jsx).
// ============================================================
const { useState: aiRptUseState } = React;

const AIR_M = ['Jan', 'Feb', 'Mar', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Des'];
const AIR_ML = ['Januar', 'Februar', 'Mars', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Desember'];
const AIR_CURY = new Date().getFullYear();
const AIR_CURM = new Date().getMonth();
const AIR_COLORS = ['var(--plum-700)', 'var(--plum-600)', 'var(--warning)', 'var(--plum-400)', 'var(--plum-500)', 'var(--plum-300)', 'var(--success)', 'var(--muted-2)'];

const airHasYear = () => false;
const airSums = () => null;
const airCats = () => [];
const airPct = (now, was) => (was ? Math.round((now - was) / Math.abs(was) * 1000) / 10 : null);
const airPctStr = (d) => (d == null ? '—' : (d > 0 ? '+' : '') + d.toLocaleString(PlummeI18n.intlLocale(), { maximumFractionDigits: 1 }) + ' %');
const AIR_EMPTY_SUMS = { inc: 0, cost: 0 };
// Rapporten her bruker grafen og resultatet, aldri saldobalansen — og saldobalansen er
// den dyreste delen av rapportmodellen. Vi ber derfor bare om det som faktisk vises.
const airBackendReports = (year, month, parts = {}) => {
  if (!window.PlummeBackend?.getReportsModel) return null;
  const options = { year, includeTrial: false, ...parts };
  if (month != null) options.months = [month, month];
  return window.PlummeBackend.getReportsModel(options);
};
const airBackendSums = (model) => model?.result
  ? { inc: model.result.inntekter || 0, cost: model.result.kostnader || 0, result: model.result.resultat }
  : null;
const airBackendCats = (model) => {
  const lines = model?.result?.sections?.find((section) => section.key === 'kostnader')?.lines || [];
  return lines
    .map((line, index) => ({
      name: line.name || line.nr || 'Kostnad',
      amount: Math.abs(line.amount || 0),
      color: AIR_COLORS[index % AIR_COLORS.length] || 'var(--muted-2)',
    }))
    .filter((line) => line.amount > 0.005);
};
const airAvailableYears = () => {
  const active = Number(window.PlummeBackend?.appState?.activeYear) || AIR_CURY;
  return [...new Set([active - 2, active - 1, active])].filter(Boolean).sort((a, b) => a - b);
};

function AiRptBars({ year, sel, monthly, chart }) {
  const inc = chart?.map((row) => row.inc || 0) || Array.from({ length: 12 }, () => 0);
  const cost = chart?.map((row) => row.cost || 0) || Array.from({ length: 12 }, () => 0);
  const W = 860, H = 230, pad = { l: 16, r: 8, t: 16, b: 28 };
  const innerH = H - pad.t - pad.b, innerW = W - pad.l - pad.r;
  const max = Math.max(...inc, ...cost, 1) * 1.12;
  const iw = innerW / 12, bw = 8;
  const y = (v) => pad.t + innerH * (1 - v / max);
  const ticks = [0, max * 0.5, max].map((v) => Math.round(v));
  return <svg viewBox={`0 0 ${W} ${H}`} width="100%" height="100%" preserveAspectRatio="none" style={{ display: 'block' }}>
    {ticks.map((tick, i) => <g key={i}>
      <line x1={pad.l} x2={W - pad.r} y1={y(tick)} y2={y(tick)} stroke="var(--border)" strokeWidth="1" />
      {i < 2 && <text x={pad.l + 2} y={y(tick) - 5} fontSize="11.5" fontWeight="700" fill="var(--muted-2)">{fmt0(tick)}</text>}
    </g>)}
    {AIR_M.map((m, i) => {
      const cx = pad.l + iw * i + iw / 2;
      const dim = monthly && sel != null && sel !== i ? 0.32 : 1;
      return <g key={m} opacity={dim}>
        <rect x={cx - bw - 1.5} y={y(inc[i])} width={bw} height={Math.max(2, y(0) - y(inc[i]))} rx="2.5" fill="var(--success)" />
        <rect x={cx + 1.5} y={y(cost[i])} width={bw} height={Math.max(2, y(0) - y(cost[i]))} rx="2.5" fill="var(--plum-500)" />
        <text x={cx} y={H - 9} fontSize="12" fontWeight={monthly && sel === i ? 900 : 700} fill={monthly && sel === i ? 'var(--plum-700)' : 'var(--muted)'} textAnchor="middle">{m}</text>
      </g>;
    })}
  </svg>;
}

// `mode` kan styres utenfra. Da følger dommen og dokumentasjonsetiketten på
// Innsikt-fanen samme valg som rapporten — ellers ville overskriften sagt «måned»
// mens tabellen viste året. Uten propen styrer rapporten seg selv som før.
function AiReport({ year: selectedYear, mode: modeProp, onModeChange }) {
  const Sec = window.PoSecHead || (({ title }) => <h3 className="t-h2">{title}</h3>);
  const years = airAvailableYears();
  const activeYear = Number(selectedYear || window.PlummeBackend?.appState?.activeYear) || AIR_CURY;
  const [innerMode, setInnerMode] = aiRptUseState('mnd');
  const mode = modeProp || innerMode;
  const setMode = (next) => (onModeChange ? onModeChange(next) : setInnerMode(next));
  const [year, setYear] = aiRptUseState(activeYear);
  const [month, setMonth] = aiRptUseState(activeYear === AIR_CURY ? AIR_CURM : activeYear < AIR_CURY ? 11 : 0);
  const [cmpYear, setCmpYear] = aiRptUseState(activeYear - 1);

  const monthly = mode === 'mnd';
  const curM = monthly ? month : null;
  const hasBackendReports = Boolean(window.PlummeBackend?.getReportsModel);
  const cmpYearEff = (cmpYear && cmpYear !== year && (hasBackendReports || airHasYear(cmpYear))) ? cmpYear : null;
  // Uten memo bygges alle tre rapportene på nytt hver gang du klikker på en måned, et
  // år eller måned/år-bryteren — også de to som ikke endret seg av klikket.
  const stateKey = window.PlummeBackend?.appState;
  const businessKey = window.PlummeBackend?.activeBusinessId;
  // Søylediagrammet er alltid hele året, så det skal ikke bygges om når du bytter måned.
  const yearModel = React.useMemo(
    () => airBackendReports(year, null, { includeResult: false }),
    [year, businessKey, stateKey],
  );
  // Tallene for valgt periode trenger ikke årsgrafen — den står allerede i yearModel.
  const curModel = React.useMemo(
    () => airBackendReports(year, curM, { includeChart: false }),
    [year, curM, businessKey, stateKey],
  );
  const cmpModel = React.useMemo(
    () => (cmpYearEff != null ? airBackendReports(cmpYearEff, curM, { includeChart: false }) : null),
    [cmpYearEff, curM, businessKey, stateKey],
  );
  if (yearModel?.blocked || curModel?.blocked || cmpModel?.blocked) return <Card><EmptyState acc="none" mood="concerned" title={t('rep.incompleteBasisTitle')} text={t('rep.incompleteBasisText')} compact /></Card>;
  const cur = airBackendSums(curModel) || airSums(year, curM) || AIR_EMPTY_SUMS;
  const cmp = cmpYearEff != null ? (airBackendSums(cmpModel) || airSums(cmpYearEff, curM) || AIR_EMPTY_SUMS) : null;

  const res = cur.result ?? cur.inc - cur.cost;
  const margin = cur.inc ? res / cur.inc * 100 : 0;
  const cmpMargin = cmp && cmp.inc ? (cmp.result ?? cmp.inc - cmp.cost) / cmp.inc * 100 : null;

  const backendCats = airBackendCats(curModel);
  const backendCmpCats = airBackendCats(cmpModel);
  const cats = backendCats.length ? backendCats : (airHasYear(year) ? airCats(year, curM) : []);
  const cmpCats = cmp ? (backendCmpCats.length ? backendCmpCats : (airHasYear(cmpYearEff) ? airCats(cmpYearEff, curM) : [])) : null;

  const periodLbl = monthly ? AIR_ML[month] + ' ' + year : 'Hele ' + year;
  const cmpLbl = monthly ? AIR_M[month] + '. ' + cmpYearEff : cmpYearEff;
  const ongoing = monthly && year === AIR_CURY && month >= AIR_CURM;

  const sub = (now, was, pp) => {
    if (was == null) return null;
    if (pp) { const d = Math.round((now - was) * 10) / 10; return (d > 0 ? '+' : '') + d.toLocaleString(PlummeI18n.intlLocale(), { maximumFractionDigits: 1 }) + ' pp mot ' + cmpYearEff; }
    return airPctStr(airPct(now, was)) + ' mot ' + cmpYearEff;
  };
  const trendOf = (now, was) => (was == null ? null : now - was);

  const dl = async () => {
    try {
      if (!window.PlummeBackend?.downloadReportPdf) throw new Error(t('r1a.report.pdfBackendUnavailable'));
      const payload = await window.PlummeBackend.downloadReportPdf({
        year,
        mode,
        month: curM,
        result: curModel?.result || null,
      });
      const link = document.createElement('a');
      link.href = `data:application/pdf;base64,${payload.pdfBase64}`;
      link.download = payload.fileName;
      link.click();
    } catch (error) {
      toast(error?.message || t('r1a.report.pdfDownloadFailed'), 'warning');
    }
  };
  const pr = () => { toast(t('r1a.report.openingPrint'), 'info'); setTimeout(() => window.print(), 350); };

  return <Card>
    <Sec icon="fileText" title={t('navgroup.reports')} sub={t('air.sub')}
      help={t('air.help')}
      right={<div className="row gap-10" style={{ alignItems: 'center' }}>
        <Button variant="secondary" size="sm" icon="printer" onClick={pr}>{t('po.print')}</Button>
        <Button variant="secondary" size="sm" icon="download" onClick={dl} data-help={t('helpTopics.exportFormat.body')} data-help-title={t('helpTopics.exportFormat.title')}>{t('air.downloadPdf')}</Button>
      </div>} />

    <div className="po-rpt-controls" data-help-title={t('air.chooseWhatToShow')} data-help={t('air.chooseHelp')}>
      <div className="po-rpt-ctl">
        <span className="po-rpt-ctl-lbl">{t('vat.view')}</span>
        <Segmented value={mode} onChange={setMode} options={[{ value: 'mnd', label: t('adv.report.monthly') }, { value: 'aar', label: t('adv.report.yearly') }]} />
      </div>
      <div className="po-rpt-ctl">
        <span className="po-rpt-ctl-lbl">{t('sharedText.rep.export.year')}</span>
        <Segmented value={year} onChange={setYear} options={years.map((y) => ({ value: y, label: String(y) }))} />
      </div>
      <div className="po-rpt-ctl po-rpt-cmp-ctl">
        <span className="po-rpt-ctl-lbl">{t('po.compareWith')}</span>
        <Segmented value={cmpYearEff || 0} onChange={(v) => setCmpYear(v || null)}
          options={[{ value: 0, label: 'Av' }, ...years.filter((y) => y !== year).map((y) => ({ value: y, label: String(y) }))]} />
      </div>
    </div>

    {monthly && <div className="po-rpt-months">
      <button className="po-rpt-mnav" onClick={() => setMonth((m) => Math.max(0, m - 1))} disabled={month === 0} aria-label={t('ap.prevMonth')}><Icon name="chevLeft" size={18} /></button>
      <div className="po-rpt-mlist">
        {AIR_M.map((m, i) => <button key={m} className={'po-rpt-mchip' + (i === month ? ' on' : '')} onClick={() => setMonth(i)}>{m}</button>)}
      </div>
      <button className="po-rpt-mnav" onClick={() => setMonth((m) => Math.min(11, m + 1))} disabled={month === 11} aria-label={t('ap.nextMonth')}><Icon name="chevRight" size={18} /></button>
    </div>}

    <div className="aii-summary" role="status">{t('adv.report.periodSummary', { period: periodLbl, income: fmt(cur.inc), costs: fmt(cur.cost), result: fmt(res) })}</div>
    <div className="po-rpt-head">
      <div>
        <div className="po-rpt-period">{periodLbl}</div>
        <div className="t-sm muted" style={{ fontWeight: 700 }}>{monthly ? t('r1a.report.subtitleMonthly') : t('r1a.report.subtitleYearly')}{cmp ? t('r1a.report.comparedWith', { period: cmpLbl }) : ''}</div>
      </div>
      {ongoing && <span className="po-rpt-ongoing"><Icon name="alertCircle" size={15} />{t('po.ongoingPeriod')}</span>}
    </div>

    <div className="po-rpt-kpis" data-help-title={t('air.keyFigures')} data-help={t('air.keyFiguresHelp')}>
      <KPI tint="green" icon="arrowUpRight" label={t('home.legend.income')} value={fmt(cur.inc)} sub={sub(cur.inc, cmp && cmp.inc)} trend={cmp ? trendOf(cur.inc, cmp.inc) : null} help={t('adv.help.income')} />
      <KPI tint="peach" icon="receipt" label={t('home.donut.costs')} value={fmt(cur.cost)} sub={sub(cur.cost, cmp && cmp.cost)} trend={cmp ? -trendOf(cur.cost, cmp.cost) : null} help={t('adv.help.costs')} />
      <KPI tint="plum" icon="trendUp" label={t('home.legend.result')} value={fmt(res)} sub={sub(res, cmp ? (cmp.result ?? cmp.inc - cmp.cost) : null)} trend={cmp ? trendOf(res, cmp.result ?? cmp.inc - cmp.cost) : null} help={t('adv.help.result')} />
      <KPI tint="white" icon="percent" label={t('air.profitMargin')} value={margin.toLocaleString(PlummeI18n.intlLocale(), { maximumFractionDigits: 1 }) + ' %'} sub={sub(margin, cmpMargin, true)} trend={cmpMargin != null ? margin - cmpMargin : null} help={t('adv.help.profitMargin')} />
    </div>

    <div className="po-rpt-chart" data-help-title={t('home.viewLabel.period')} data-help={t('air.chartHelp')}>
      <div className="po-rpt-chart-head">
        <div className="t-h3" style={{ fontSize: 15 }}>{t('r1a.report.chartTitle', { year })}</div>
        <div className="po-rpt-legend">
          <span><span className="po-rpt-sw" style={{ background: 'var(--success)' }} />{t('home.legend.income')}</span>
          <span><span className="po-rpt-sw" style={{ background: 'var(--plum-500)' }} />{t('home.legend.cost')}</span>
        </div>
      </div>
      <div style={{ aspectRatio: '3.7 / 1', overflow: 'hidden' }}><AiRptBars year={year} sel={month} monthly={monthly} chart={yearModel?.chart} /></div>
    </div>

    <div className={'po-rpt-table' + (cmp ? ' cmp' : '')} data-help-title={t('air.costsByCategory')} data-help={t('air.costsByCategoryHelp')}>
      <div className="po-rpt-tr po-rpt-th">
        <span>{t('air.costCategory')}</span>
        <span className="num">{monthly ? AIR_ML[month] + ' ' + year : year}</span>
        {cmp && <span className="num">{cmpLbl}</span>}
        {cmp && <span className="num" data-help-title={t('clo.movement')} data-help={t('adv.help.movement')}>{t('clo.movement')}</span>}
      </div>
      {cats.map((c, i) => {
        const was = cmpCats?.[i]?.amount ?? null;
        const d = was != null ? airPct(c.amount, was) : null;
        return <div key={c.name} className="po-rpt-tr">
          <span className="po-rpt-cat"><span className="po-rpt-dot" style={{ background: c.color }} />{c.name}</span>
          <span className="num">{fmt(c.amount)}</span>
          {cmp && <span className="num muted">{fmt(was)}</span>}
          {cmp && <span className="num po-rpt-delta" style={{ color: d > 0 ? 'var(--error)' : d < 0 ? 'var(--success)' : 'var(--muted)' }}>
            {d !== 0 && d != null && <Icon name={d > 0 ? 'trendUp' : 'trendDown'} size={13} />}{airPctStr(d)}
          </span>}
        </div>;
      })}
      <div className="po-rpt-tr po-rpt-total">
        <span>{t('po.totalCosts')}</span>
        <span className="num">{fmt(cur.cost)}</span>
        {cmp && <span className="num muted">{fmt(cmp.cost)}</span>}
        {cmp && <span className="num po-rpt-delta" style={{ color: airPct(cur.cost, cmp.cost) > 0 ? 'var(--error)' : 'var(--success)' }}>{airPctStr(airPct(cur.cost, cmp.cost))}</span>}
      </div>
    </div>

    <div className="t-xs muted" style={{ fontWeight: 600, marginTop: 14 }}>{t('r1a.report.footnote')}</div>
  </Card>;
}

// ============================================================
// Periodeoppsummering — «Tre ting gikk bra / To ting bør følges / Én anbefaling»
// ============================================================
// Bygger på de samme observasjonene som hendelsesstrømmen på Oversikt: grønne har
// gått bra, gule og røde bør følges. Vi utleder ikke nye funn her — det ville gitt to
// steder som kunne si ulike ting om samme regnskap.
function AiiReportRecap({ feed = [], recommendation, onAct }) {
  const good = feed.filter((item) => item.tone === 'green').slice(0, 3);
  const watch = feed.filter((item) => item.tone !== 'green').slice(0, 2);
  if (!good.length && !watch.length && !recommendation) return null;
  const label = (item) => t('adv.feed.' + item.key + '.title', window.aiiFeedVars ? window.aiiFeedVars(item.vars) : item.vars);
  return <Card>
    <window.AiSecHead icon="fileText" title={t('adv.recap.title')} sub={t('adv.recap.sub')} help={t('adv.recap.help')} />
    <div className="aii-recap" style={{ marginTop: 14 }}>
      <div className="aii-recap-col good">
        <div className="aii-recap-lbl"><Icon name="checkCircle" size={16} />{t('adv.recap.good', { count: good.length })}</div>
        {good.length
          ? <ul>{good.map((item, i) => <li key={i}>{label(item)}</li>)}</ul>
          : <p className="t-sm muted">{t('adv.recap.noGood')}</p>}
      </div>
      <div className="aii-recap-col watch">
        <div className="aii-recap-lbl"><Icon name="alertCircle" size={16} />{t('adv.recap.watch', { count: watch.length })}</div>
        {watch.length
          ? <ul>{watch.map((item, i) => <li key={i}>{label(item)}</li>)}</ul>
          : <p className="t-sm muted">{t('adv.recap.noWatch')}</p>}
      </div>
      <div className="aii-recap-col reco">
        <div className="aii-recap-lbl"><Icon name="sparkle" size={16} />{t('adv.recap.reco')}</div>
        {recommendation
          ? <React.Fragment>
            <p>{recommendation.title || recommendation.text}</p>
            {recommendation.route && <button type="button" className="aii-move-btn" onClick={() => onAct && onAct(recommendation.route)}>
              {recommendation.action}<Icon name="arrowRight" size={15} />
            </button>}
          </React.Fragment>
          : <p className="t-sm muted">{t('adv.recap.noReco')}</p>}
      </div>
    </div>
  </Card>;
}

Object.assign(window, { AiReport, AiiReportRecap });
